// NavBar.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Dropdown, Menu, Avatar } from 'antd';
import {
  MenuOutlined,
  CloseOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  DesktopOutlined,
  DollarOutlined,
  CalendarOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import logo from '../pages/login/assets/login-illustration.png'; // adjust path if necessary

export default function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // localStorage-driven auth info
  const token = localStorage.getItem('token') || null;
  const fullName = localStorage.getItem('fullName') || null;
  const role = localStorage.getItem('role') || null;
  const isAuthenticated = Boolean(token);

  const navItems = [
    'Leads',
    'Demo',
    'Nurturing',
    'First Installments',
    'Pending Installments',
    'Enrollments',
  ];

  const slugify = (text) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const handleNavClick = (key) => {
    // role-based Home handled separately when clicking logo
    const path = key === 'Home' ? '/' : `/${slugify(key)}`;
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogoNavigate = () => {
    const r = (localStorage.getItem('role') || '').toUpperCase();
    if (r === 'ADMIN') handleNavClick('admin-home');
    else handleNavClick('user-home');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tempToken');
    localStorage.removeItem('email');
    localStorage.removeItem('fullName');
    localStorage.removeItem('role');
    navigate('/login');
    setMobileOpen(false);
  };

  // small helper for keyboard activation
  const handleKeyActivate = (e, cb) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      cb();
    }
  };

  // initials for avatar fallback
  const getInitials = (name = '') =>
    name
      .split(' ')
      .map((n) => n[0] || '')
      .slice(0, 2)
      .join('')
      .toUpperCase();

  // Dropdown menu
  const userMenu = (
    <Menu selectable={false} className="overflow-hidden rounded-lg shadow-md">
      <Menu.Item
        key="profile"
        icon={<UserOutlined />}
        onClick={() => handleNavClick('Profile')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
        Profile
      </Menu.Item>

      <Menu.Item
        key="demo-batch-dates"
        icon={<DesktopOutlined />}
        onClick={() => handleNavClick('Demo-Batch-Dates')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
       Update Demo Dates
      </Menu.Item>

      {/* ✅ Show only to ADMIN */}
    {role?.toUpperCase() === 'ADMIN' && (
      <Menu.Item
        key="payment-links"
        icon={<DollarOutlined />}
        onClick={() => handleNavClick('Payment-Links')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
        Update Payment Links
      </Menu.Item> 

     
    )}

    {role?.toUpperCase() === 'ADMIN' && (
      <Menu.Item
        key="update-users"
        icon={<DollarOutlined />}
        onClick={() => handleNavClick('Update-Users')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
        Update Users
      </Menu.Item> 

     
    )}

      <Menu.Item
        key="upcoming-batch"
        icon={<CalendarOutlined />}
        onClick={() => handleNavClick('Upcoming-Batch')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
        Upcoming Batch
      </Menu.Item>

      <Menu.Item
        key="drop-lead"
        icon={<DeleteOutlined />}
        onClick={() => handleNavClick('Drop-Lead')}
        className="!font-medium !text-gray-900 hover:!bg-blue-50 hover:!text-blue-700 transition-all duration-200"
      >
        Drop Leads
      </Menu.Item>

      <Menu.Divider />

      <Menu.Item
        key="logout"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        className="!font-semibold !text-white !bg-red-600 hover:!bg-red-700 active:!bg-red-800 transition-all duration-200 rounded-md text-center"
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-white shadow-lg">
      <div className="w-full px-4 overflow-x-auto sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 ">
          {/* Left: Logo & title (focusable) */}
          <div
            className="flex items-center min-w-0 gap-0 cursor-pointer"
            onClick={handleLogoNavigate}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleKeyActivate(e, handleLogoNavigate)}
            aria-label="Go to home"
            title="Go to Home"
          >
            <div className="flex-shrink-0">
              <img
                src={logo}
                alt="Practical Infosec Logo"
                className="object-contain rounded-md h-14 w-14 sm:h-14 sm:w-14"
              />
            </div>

            <div className="flex flex-col min-w-0 leading-tight">
              <span className="text-2xl font-extrabold text-black truncate">Practical Infosec</span>
              <span className="text-xs text-gray-700 font-semibold -mt-0.5 truncate">
                Powerful CRM for modern businesses
              </span>
            </div>
          </div>

          {/* Nav - show horizontally from tablet up (sm and above), mobile uses drawer */}
          {isAuthenticated && (
            <nav className="hidden sm:flex sm:items-center sm:gap-3 flex-nowrap" aria-label="Primary">
              {navItems.map((item) => {
                const path = `/${slugify(item)}`;
                const isActive =
                  location.pathname === path || (location.pathname === '/' && item === 'Home');
                return (
                  <button
                    key={item}
                    onClick={() => handleNavClick(item)}
                    onKeyDown={(e) => handleKeyActivate(e, () => handleNavClick(item))}
                    type="button"
                    aria-current={isActive ? 'page' : undefined}
                    className={`px-4 py-3 sm:px-5 sm:py-3 whitespace-nowrap text-lg font-bold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500
                      ${isActive ? 'bg-black text-white shadow-sm' : 'text-black hover:bg-gray-400 hover:text-black'}`}
                  >
                    {item}
                  </button>
                );
              })}
            </nav>
          )}

          {/* Right: auth / avatar */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex sm:items-center sm:gap-3">
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-md font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  Login
                </Link>
              ) : (
                <div className="flex items-center gap-3">
                  <Dropdown overlay={userMenu} placement="bottomRight" trigger={['click']}>
                    <div
                      className="flex items-center gap-3 px-2 py-1 rounded-md cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                      tabIndex={0}
                      role="button"
                      aria-haspopup="menu"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click();
                      }}
                    >
                      {fullName ? (
                        <Avatar style={{ backgroundColor: '#fde68a', color: '#92400e' }}>
                          {getInitials(fullName)}
                        </Avatar>
                      ) : (
                        <Avatar icon={<UserOutlined />} />
                      )}

                      <div className="hidden min-w-0 sm:flex sm:flex-col">
                        <span className="text-sm font-semibold text-gray-900 truncate">{fullName}</span>
                        <span className="mt-0.5 inline-block px-1.5 py-0.5 text-[11px] font-medium  text-black rounded-md leading-none">
                          {role}
                        </span>
                      </div>

                      <DownOutlined className="ml-1 text-sm font-bold text-gray-900" />
                    </div>
                  </Dropdown>
                </div>
              )}
            </div>

            {/* Mobile hamburger (visible on xs only) */}
            <div className="sm:hidden">
              <button
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((s) => !s)}
                className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                type="button"
              >
                {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="bg-white border-t sm:hidden" role="dialog" aria-modal="true">
          <div className="px-4 py-4 space-y-3">
            {isAuthenticated &&
              navItems.map((item) => {
                const path = `/${slugify(item)}`;
                const isActive = location.pathname === path || (location.pathname === '/' && item === 'Home');

                return (
                  <button
                    key={item}
                    onClick={() => handleNavClick(item)}
                    onKeyDown={(e) => handleKeyActivate(e, () => handleNavClick(item))}
                    className={`w-full px-3 py-3 text-md font-semibold text-left rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300
                      ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100 hover:text-blue-700'}`}
                    type="button"
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item}
                  </button>
                );
              })}

            <div className="pt-2 border-t">
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-md font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
                  onClick={() => setMobileOpen(false)}
                >
                  Login
                </Link>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {fullName ? (
                      <Avatar style={{ backgroundColor: '#fde68a', color: '#92400e' }}>
                        {getInitials(fullName)}
                      </Avatar>
                    ) : (
                      <Avatar icon={<UserOutlined />} />
                    )}

                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{fullName}</div>
                      {role && (
                        <div className="mt-1 inline-block px-1.5 py-0.5 text-[11px] font-medium text-white bg-black rounded-md leading-none">
                          {role}
                        </div>
                      )}
                    </div>
                  </div> 

                  <Button
                    block
                    onClick={() => {
                      handleLogout();
                      setMobileOpen(false);
                    }}
                    className="!bg-red-600 !text-white !border-none hover:!bg-red-700 active:!bg-red-800 transition-all duration-200 font-bold"
                  >
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
